<template>
  <div class="video-control-bar"
    @click="$emit('togglePlay')"
  >
    <div class="control-top" v-if="title && isFullscreen">
      <span class="title-text">{{ title }}</span>
    </div>
    <div class="control-bottom" @click.stop>
    <div class="progress-row">
      <div class="time-row">
        <span class="time-label">{{ formatTime(currentTime, duration) }}</span>
        <span class="time-label">{{ formatTime(duration) }}</span>
      </div>
      <div class="progress-bar" ref="progressTrack" @click="handleProgressClick">
        <div class="progress-track">
          <div class="progress-fill" :style="{ width: progressPercent + '%' }"></div>
          <div class="progress-thumb" :style="{ left: progressPercent + '%' }"></div>
        </div>
      </div>
    </div>
    <div class="action-row">
      <div class="control-buttons">
        <button
          v-if="showPrev"
          class="icon-btn"
          :disabled="disablePrev || loading"
          @click="$emit('prev')"
        >
          <img :src="iconPrev" alt="prev">
        </button>
        <button
          class="icon-btn primary"
          :disabled="disabledPlay || loading"
          @click="$emit('togglePlay')"
        >
          <img :src="playIcon" alt="play" />
        </button>
        <button
          v-if="showNext"
          class="icon-btn"
          :disabled="disableNext || loading"
          @click="$emit('next')"
        >
          <img :src="iconNext" alt="next" />
        </button>
      </div>
      <div class="control-actions">
        <div
          v-if="showListBtn"
          class="list-button-wrapper"
          @mouseenter="handleListMouseEnter"
          @mouseleave="handleListMouseLeave"
        >
          <button class="text-btn">课时列表</button>
          <transition name="fade">
            <div v-if="showListModal" class="chapter-modal">
              <div class="chapter-modal__body">
                <ul class="chapter-list" ref="chapterListBody" @scroll="handleChapterScroll">
                  <li
                    v-for="(chapter, index) in chapters"
                    :key="index"
                    :class="['chapter-item', { active: index === currentIndex }]"
                    @click="handleSelectChapter(index)"
                  > {{chapter.title || '-'}}
                  </li>
                </ul>
              </div>
            </div>
          </transition>
        </div>
        <button
          v-if="showFullscreen"
          class="icon-btn"
          :disabled="disableFullscreen || loading"
          @click="$emit('fullscreen')"
        >
          <img :src="fullscreenIcon" alt="fullscreen" />
        </button>
        <slot />
      </div>
    </div>
    </div>
  </div>
</template>

<script>
import iconPlay from './images/ic_play.svg';
import iconPause from './images/ic_pause.svg';
import iconPrev from './images/ic_prev.svg';
import iconNext from './images/ic_next.svg';
import iconFullscreen from './images/ic_fullscreen.svg';
import iconExitFullscreen from './images/ic_exitFullscreen.svg';
import { formatDuration } from '@/common/js/util';

export default {
  name: 'VideoControlBar',
  data() {
    return {
      iconPrev,
      iconNext,
      showListModal: false,
      listHideTimer: null,
    };
  },
  props: {
    visible: Boolean,
    title: {
      type: String,
      default: '',
    },
    currentTime: {
      type: Number,
      default: 0,
    },
    duration: {
      type: Number,
      default: 0,
    },
    isPlaying: {
      type: Boolean,
      default: false,
    },
    showPrev: {
      type: Boolean,
      default: true,
    },
    showNext: {
      type: Boolean,
      default: true,
    },
    showFullscreen: {
      type: Boolean,
      default: true,
    },
    disablePrev: {
      type: Boolean,
      default: false,
    },
    disableNext: {
      type: Boolean,
      default: false,
    },
    disableFullscreen: {
      type: Boolean,
      default: false,
    },
    disabledPlay: {
      type: Boolean,
      default: false,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    isFullscreen: {
      type: Boolean,
      default: false,
    },
    chapters: {
      type: Array,
      default: () => [],
    },
    currentIndex: {
      type: Number,
      default: 0,
    },
  },
  computed: {
    playIcon() {
      return this.isPlaying ? iconPause : iconPlay;
    },
    fullscreenIcon() {
      return this.isFullscreen ? iconExitFullscreen : iconFullscreen;
    },
    progressPercent() {
      if (!this.duration) return 0;
      return Math.min(100, (this.currentTime / this.duration) * 100);
    },
    showListBtn() {
      return this.isFullscreen && this.chapters.length > 1;
    }
  },
  methods: {
    handleProgressClick(event) {
      if (!this.duration || this.loading || this.disabledPlay) return;
      const track = this.$refs.progressTrack?.querySelector('.progress-track');
      if (!track) return;
      const rect = track.getBoundingClientRect();
      const percent = (event.clientX - rect.left) / rect.width;
      const normalized = Math.max(0, Math.min(1, percent));
      this.$emit('seek', normalized);
    },
    formatTime(seconds, maxSeconds=Infinity) {
      return formatDuration(Math.min(seconds, maxSeconds));
    },
    handleListMouseEnter() {
      if (!this.showListBtn) return;
      clearTimeout(this.listHideTimer);
      this.showListModal = true;
      this.$nextTick(() => {
        this.scrollChapterListToCurrent();
      });
    },
    handleListMouseLeave() {
      clearTimeout(this.listHideTimer);
      this.listHideTimer = setTimeout(() => {
        this.showListModal = false;
      }, 200);
    },
    handleSelectChapter(index) {
      if (index === this.currentIndex) {
        return;
      }
      this.$nextTick(() => {
        this.showListModal = false;
      })
      this.$emit('selectChapter', index);
    },
    scrollChapterListToCurrent() {
      const container = this.$refs.chapterListBody;
      if (!container) return;
      const active = container.querySelector('.chapter-item.active');
      if (!active) {
        container.scrollTop = 0;
        return;
      }
      const offset = active.offsetTop - container.clientHeight / 2 + active.clientHeight / 2;
      container.scrollTop = Math.max(0, offset);
    },
    handleChapterScroll() {
      if (!this.showListModal) return;
      this.$emit('chapterScroll')
    }
  },
  watch: {
    currentIndex() {
      if (this.showListModal) {
        this.$nextTick(() => {
          this.scrollChapterListToCurrent();
        });
      }
    },
    visible() {
      if (!this.visible) {
        this.showListModal = false;
      }
    }
  },
  beforeDestroy() {
    clearTimeout(this.listHideTimer);
  },
};
</script>

<style scoped>
.video-control-bar {
  height: 100%;
  color: #fff;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  user-select: none;
}
.control-top {
  background: linear-gradient(180deg, rgba(0,0,0,0.5) 0%, rgba(0, 0, 0, 0) 100%);
  height: 120px;
  padding: 24px 20px;
  box-sizing: border-box;
}
.control-top .title-text {
  font-size: 20px;
  font-weight: 600;
}
.control-bottom {
  background: linear-gradient(180deg, rgba(0,0,0,0) 0%, rgba(0, 0, 0, 0.6) 100%);
  margin-top: auto;
  padding: 0 20px 18px;
}
.control-buttons,
.control-actions {
  display: flex;
  align-items: center;
  gap: 16px;
}
.time-row, .action-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.time-row {
  margin-bottom: 8px;
  font-size: 14px;
}
.action-row {
  margin-top: 13px;
}
.icon-btn {
  cursor: pointer;
  background: transparent;
}

.icon-btn:disabled {
  opacity: 0.35;
  cursor: not-allowed;
}

.text-btn {
  cursor: pointer;
  background: rgba(0, 0, 0, 0.5);
  border-radius: 50px;
  color: #fff;
  padding: 6px 16px;
  font-size: 14px;
}

/* .icon-btn:not(:disabled):hover {
  background: rgba(255, 255, 255, 0.2);
} */

.icon-btn img {
  width: 1.67vw;
  max-width: 64px;
  min-width: 30px;
  display: block;
}

.progress-bar {
  flex: 1;
  cursor: pointer;
}

.progress-track {
  position: relative;
  width: 100%;
  height: 6px;
  background: rgba(235, 235, 235, .4);
  border-radius: 999px;
}

.progress-fill {
  position: absolute;
  left: 0;
  top: 0;
  height: 100%;
  border-radius: inherit;
  /* background: linear-gradient(90deg, #ff6f61, #ff944d); */
  background: #E60012;
}

.progress-thumb {
  position: absolute;
  top: 50%;
  width: 14px;
  height: 14px;
  border-radius: 50%;
  background: #E60012;
  transform: translate(-50%, -50%);
  /* box-shadow: 0 2px 6px rgba(255, 111, 97, 0.4); */
}

.list-button-wrapper {
  position: relative;
}

.chapter-modal {
  position: absolute;
  right: -50px;
  bottom: calc(100% + 4px);
  z-index: 10;
  display: flex;
  align-items: center;
  justify-content: center;
}

.chapter-modal__body {
  width: 360px;
  background: rgba(0, 0, 0, 0.7);
  border-radius: 10px;
  padding: 8px 0;
}

.chapter-list {
  list-style: none;
  padding: 0;
  margin: 0;
  max-height: 320px;
  min-height: 180px;
  overflow-y: auto;
}

.chapter-list::-webkit-scrollbar {
  width: 6px;
}

.chapter-list::-webkit-scrollbar-thumb {
  border-radius: 90px;
  height: 146px;
  background: rgba(255, 255, 255, .3);
}

.chapter-item {
  display: flex;
  padding: 0 16px;
  height: 40px;
  line-height: 40px;
  font-weight: 500;
  font-size: 14px;
  color: #fff;
  cursor: pointer;
  transition: background 0.2s ease;
  align-items: baseline;
}

.chapter-item:hover {
  background: rgba(77, 76, 76, 1);
}

.chapter-item.active {
  color: #FF1729;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}

.fade-enter,
.fade-leave-to {
  opacity: 0;
}
</style>

